import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import type { AIModel, PlatformSettings } from '../hooks/useAdminData';
import {
  useDeleteModel,
  useSettings,
  useUpdateSettings,
  useUpsertModel,
} from '../hooks/useAdminData';

type PlanLimitsMap = PlatformSettings['planLimits'];
type PlanLimitEntry = PlanLimitsMap[string];

const PLAN_ORDER = ['free', 'starter', 'growth', 'professional', 'business', 'enterprise'];
const MODEL_TIERS = ['fast', 'balanced', 'premium'];
const MAX_USD_TO_COP_RATE = '4482.890050';
const RATE_PATTERN = /^[1-9]\d*(?:\.\d{1,6})?$/;

function parseRateMicros(value: string): bigint | null {
  if (value.length > 20 || !RATE_PATTERN.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  return BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0'));
}

function validateExchangeRate(value: string): string | null {
  const rate = parseRateMicros(value);
  if (rate === null) return 'Enter a positive rate with up to 6 decimal places.';
  if (rate > parseRateMicros(MAX_USD_TO_COP_RATE)!) {
    return `Rate must not exceed ${MAX_USD_TO_COP_RATE}.`;
  }
  return null;
}

function planBadgeVariant(plan: string): 'default' | 'info' | 'success' | 'primary' {
  switch (plan.toLowerCase()) {
    case 'starter':
      return 'info';
    case 'growth':
    case 'pro':
      return 'success';
    case 'scale':
      return 'success';
    case 'enterprise':
      return 'primary';
    default:
      return 'default';
  }
}

function formatNumber(n: number) {
  return n.toLocaleString();
}

function Card({
  title,
  children,
  onEdit,
  editing,
  editLabel,
}: {
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
  editing?: boolean;
  editLabel?: string;
}) {
  return (
    <div className="bg-surface rounded-xl border border-border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-secondary">{title}</h2>
        {onEdit && !editing && (
          <button
            onClick={onEdit}
            aria-label={editLabel}
            className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text transition-colors"
          >
            <Pencil size={14} />
            Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function ToggleSwitch({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
        enabled ? 'bg-primary' : 'bg-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function PlanLimitsEditor({
  planLimits,
  onSave,
  onCancel,
  saving,
}: {
  planLimits: PlanLimitsMap;
  onSave: (updated: PlanLimitsMap) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<PlanLimitsMap>(JSON.parse(JSON.stringify(planLimits)));

  function updateField(
    plan: string,
    field: keyof PlanLimitEntry,
    value: number | string | boolean,
  ) {
    setDraft((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [field]: value },
    }));
  }

  const sortedPlans = Object.entries(draft).sort(
    ([a], [b]) => PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b),
  );

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th className="px-4 py-2 font-medium">Plan</th>
              <th className="px-4 py-2 font-medium">Max Leads</th>
              <th className="px-4 py-2 font-medium">Max Seats</th>
              <th className="px-4 py-2 font-medium">Model Tier</th>
              <th className="px-4 py-2 font-medium">Voice</th>
              <th className="px-4 py-2 font-medium">WhatsApp</th>
              <th className="px-4 py-2 font-medium">Custom Prompts</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlans.map(([plan, limits]) => (
              <tr key={plan} className="border-b border-border">
                <td className="px-4 py-2">
                  <Badge variant={planBadgeVariant(plan)}>{plan}</Badge>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    value={limits.maxLeads}
                    onChange={(e) => updateField(plan, 'maxLeads', Number(e.target.value))}
                    className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-text w-24"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    value={limits.maxSeats}
                    onChange={(e) => updateField(plan, 'maxSeats', Number(e.target.value))}
                    className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-text w-24"
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={limits.modelTier}
                    onChange={(e) => updateField(plan, 'modelTier', e.target.value)}
                    className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-text"
                  >
                    {MODEL_TIERS.map((tier) => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <ToggleSwitch
                    enabled={limits.voiceEnabled}
                    onToggle={() => updateField(plan, 'voiceEnabled', !limits.voiceEnabled)}
                  />
                </td>
                <td className="px-4 py-2">
                  <ToggleSwitch
                    enabled={limits.whatsappEnabled}
                    onToggle={() => updateField(plan, 'whatsappEnabled', !limits.whatsappEnabled)}
                  />
                </td>
                <td className="px-4 py-2">
                  <ToggleSwitch
                    enabled={limits.customPrompts}
                    onToggle={() => updateField(plan, 'customPrompts', !limits.customPrompts)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 justify-end">
        <button
          onClick={onCancel}
          disabled={saving}
          className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
        >
          <X size={14} className="inline mr-1.5" />
          Cancel
        </button>
        <button
          onClick={() => onSave(draft)}
          disabled={saving}
          className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
        >
          <Check size={14} className="inline mr-1.5" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// Must stay in step with PROVIDER_MODELS on the backend: the API rejects a
// provider the model router cannot serve, so offering one here only produces a
// failed save.
const PROVIDERS = ['anthropic', 'google'];

function ModelsCard({ models }: { models: AIModel[] }) {
  const upsertMutation = useUpsertModel();
  const deleteMutation = useDeleteModel();
  const [editing, setEditing] = useState<AIModel | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const emptyModel: AIModel = {
    key: '',
    provider: 'anthropic',
    model: '',
    inputPerMTok: 0,
    cachedInputPerMTok: 0,
    outputPerMTok: 0,
    batchInputPerMTok: 0,
    batchOutputPerMTok: 0,
    isEnabled: true,
    source: 'custom',
  };

  function handleSave(m: AIModel) {
    upsertMutation.mutate(
      {
        model: m.key,
        provider: m.provider,
        providerModelId: m.model !== m.key ? m.model : undefined,
        inputPerMTok: m.inputPerMTok,
        cachedInputPerMTok: m.cachedInputPerMTok,
        outputPerMTok: m.outputPerMTok,
        batchInputPerMTok: m.batchInputPerMTok,
        batchOutputPerMTok: m.batchOutputPerMTok,
        isEnabled: m.isEnabled,
      },
      {
        onSuccess: () => {
          setEditing(null);
          setAdding(false);
        },
      },
    );
  }

  function handleDelete(key: string) {
    deleteMutation.mutate(key, {
      onSuccess: () => setConfirmDelete(null),
    });
  }

  const showForm = editing || adding;
  const formModel = editing ?? emptyModel;

  return (
    <div className="bg-surface rounded-xl border border-border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-secondary">Supported AI Models</h2>
        {!showForm && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover transition-colors"
          >
            <Plus size={14} />
            Add Model
          </button>
        )}
      </div>

      {showForm ? (
        <ModelForm
          model={formModel}
          isNew={adding}
          saving={upsertMutation.isPending}
          onSave={handleSave}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
          }}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Input/MTok</th>
                <th className="px-3 py-2 font-medium">Output/MTok</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium w-24" />
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr
                  key={m.key}
                  className={`border-b border-border ${!m.isEnabled ? 'opacity-50' : ''}`}
                >
                  <td className="px-3 py-2 font-mono text-text text-xs">
                    {m.key}
                    {/* The row key is a tier alias, so `gemini-flash` alone does
                        not say which generation it resolves to. Shown only when
                        the two differ — for Anthropic the alias is the wire id. */}
                    {m.model !== m.key && (
                      <div className="text-text-muted" title="Provider model ID">
                        {m.model}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-text-secondary capitalize">{m.provider}</td>
                  <td className="px-3 py-2 text-text-secondary">${m.inputPerMTok}</td>
                  <td className="px-3 py-2 text-text-secondary">${m.outputPerMTok}</td>
                  <td className="px-3 py-2">
                    <Badge variant={m.isEnabled ? 'success' : 'default'}>
                      {m.isEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={m.source === 'hardcoded' ? 'default' : 'info'}>
                      {m.source}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => setEditing(m)}
                        className="p-1.5 rounded text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {confirmDelete === m.key ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(m.key)}
                            disabled={deleteMutation.isPending}
                            className="p-1.5 rounded text-red-400 hover:bg-red-400/10 transition-colors"
                            title="Confirm delete"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="p-1.5 rounded text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(m.key)}
                          className="p-1.5 rounded text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ModelForm({
  model,
  isNew,
  saving,
  onSave,
  onCancel,
}: {
  model: AIModel;
  isNew: boolean;
  saving: boolean;
  onSave: (m: AIModel) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<AIModel>({ ...model });

  function update<K extends keyof AIModel>(field: K, value: AIModel[K]) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  const canSave =
    draft.key.trim() && draft.provider && draft.inputPerMTok >= 0 && draft.outputPerMTok >= 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Model Key</label>
          <input
            type="text"
            value={draft.key}
            onChange={(e) => update('key', e.target.value)}
            disabled={!isNew}
            placeholder="e.g. gpt-4.1-nano"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text font-mono disabled:opacity-50"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Provider</label>
          <select
            value={draft.provider}
            onChange={(e) => update('provider', e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-text-muted block">
          Provider Model ID (optional, if different from key)
        </label>
        <input
          type="text"
          value={draft.model !== draft.key ? draft.model : ''}
          onChange={(e) => update('model', e.target.value || draft.key)}
          placeholder={draft.key || 'Same as model key'}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text font-mono"
        />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Input $/MTok</label>
          <input
            type="number"
            step="0.001"
            min={0}
            value={draft.inputPerMTok}
            onChange={(e) => update('inputPerMTok', Number(e.target.value))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Cached Input $/MTok</label>
          <input
            type="number"
            step="0.001"
            min={0}
            value={draft.cachedInputPerMTok}
            onChange={(e) => update('cachedInputPerMTok', Number(e.target.value))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Output $/MTok</label>
          <input
            type="number"
            step="0.001"
            min={0}
            value={draft.outputPerMTok}
            onChange={(e) => update('outputPerMTok', Number(e.target.value))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-text-muted block">Batch Output $/MTok</label>
          <input
            type="number"
            step="0.001"
            min={0}
            value={draft.batchOutputPerMTok}
            onChange={(e) => update('batchOutputPerMTok', Number(e.target.value))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs text-text-muted">Enabled</label>
        <ToggleSwitch
          enabled={draft.isEnabled}
          onToggle={() => update('isEnabled', !draft.isEnabled)}
        />
      </div>
      <div className="flex items-center gap-3 justify-end pt-2 border-t border-border">
        <button
          onClick={onCancel}
          disabled={saving}
          className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
        >
          <X size={14} className="inline mr-1.5" />
          Cancel
        </button>
        <button
          onClick={() => canSave && onSave(draft)}
          disabled={saving || !canSave}
          className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
        >
          <Check size={14} className="inline mr-1.5" />
          {saving ? 'Saving...' : isNew ? 'Add Model' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export function Settings() {
  const { data, isLoading } = useSettings();
  const updateMutation = useUpdateSettings();

  const [editingPlanLimits, setEditingPlanLimits] = useState(false);
  const [editingRateLimits, setEditingRateLimits] = useState(false);
  const [editingDefaults, setEditingDefaults] = useState(false);
  const [editingExchangeRate, setEditingExchangeRate] = useState(false);
  const [exchangeRateDraft, setExchangeRateDraft] = useState('');

  const [rateDraft, setRateDraft] = useState<{ ttl: number; limit: number } | null>(null);
  const [defaultsDraft, setDefaultsDraft] = useState<{
    defaultMonthlyTokenLimit: number;
    defaultModelTier: string;
  } | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface rounded-xl border border-border p-5 h-32 animate-pulse"
            >
              <div className="h-4 bg-border rounded w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-text-muted">Failed to load settings.</p>
      </div>
    );
  }

  const sortedPlans = Object.entries(data.planLimits).sort(
    ([a], [b]) => PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b),
  );

  function handleSavePlanLimits(updated: PlanLimitsMap) {
    updateMutation.mutate(
      { planLimits: updated },
      { onSuccess: () => setEditingPlanLimits(false) },
    );
  }

  function handleSaveRateLimits() {
    if (!rateDraft) return;
    updateMutation.mutate(
      { rateLimitTtl: rateDraft.ttl, rateLimitMax: rateDraft.limit },
      {
        onSuccess: () => {
          setEditingRateLimits(false);
          setRateDraft(null);
        },
      },
    );
  }

  function handleSaveDefaults() {
    if (!defaultsDraft) return;
    updateMutation.mutate(
      {
        defaultMonthlyTokenLimit: defaultsDraft.defaultMonthlyTokenLimit,
        defaultModelTier: defaultsDraft.defaultModelTier,
      },
      {
        onSuccess: () => {
          setEditingDefaults(false);
          setDefaultsDraft(null);
        },
      },
    );
  }

  function handleSaveExchangeRate() {
    if (validateExchangeRate(exchangeRateDraft)) return;
    updateMutation.mutate(
      { usdToCop: exchangeRateDraft },
      {
        onSuccess: () => {
          setEditingExchangeRate(false);
          setExchangeRateDraft('');
        },
      },
    );
  }

  const exchangeRateError = validateExchangeRate(exchangeRateDraft);
  const usdToCop = typeof data.usdToCop === 'string' ? data.usdToCop : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <Card title="Billing Provider">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-text">Provider:</span>
            <span className="text-sm text-text font-medium capitalize">{data.billingProvider}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-text">Stripe:</span>
            {data.stripeConfigured ? (
              <Badge variant="success">Configured</Badge>
            ) : (
              <Badge variant="warning">Not Configured</Badge>
            )}
          </div>
        </div>
        {!data.stripeConfigured && (
          <p className="text-sm text-text-muted">
            Stripe integration coming soon. Currently using manual billing.
          </p>
        )}
        <button
          disabled
          className="px-4 py-2 text-sm rounded-lg bg-primary text-background font-medium opacity-50 cursor-not-allowed"
        >
          Configure Stripe
          <span className="ml-2 text-xs opacity-70">(Coming soon)</span>
        </button>
      </Card>

      <Card
        title="USD to COP Exchange Rate"
        onEdit={() => {
          setExchangeRateDraft(usdToCop ?? '');
          setEditingExchangeRate(true);
        }}
        editing={editingExchangeRate}
        editLabel="Edit USD to COP exchange rate"
      >
        {editingExchangeRate ? (
          <div className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="usd-to-cop-rate" className="text-xs text-text-muted block">
                COP pesos per USD
              </label>
              <input
                id="usd-to-cop-rate"
                type="text"
                inputMode="decimal"
                value={exchangeRateDraft}
                onChange={(event) => setExchangeRateDraft(event.target.value)}
                aria-invalid={!!exchangeRateError}
                aria-describedby={exchangeRateError ? 'usd-to-cop-error' : undefined}
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text w-48"
              />
              {exchangeRateError ? (
                <p id="usd-to-cop-error" role="alert" className="text-xs text-red-400">
                  {exchangeRateError}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setEditingExchangeRate(false);
                  setExchangeRateDraft('');
                }}
                disabled={updateMutation.isPending}
                className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
              >
                <X size={14} className="inline mr-1.5" />
                Cancel
              </button>
              <button
                onClick={handleSaveExchangeRate}
                disabled={updateMutation.isPending || !!exchangeRateError}
                aria-label="Save exchange rate"
                className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                <Check size={14} className="inline mr-1.5" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-lg font-medium text-text">
              {usdToCop
                ? `1 USD = ${Number(usdToCop).toLocaleString('en-US', { maximumFractionDigits: 6 })} COP`
                : 'Exchange rate unavailable'}
            </p>
            <p className="text-sm text-text-muted">
              Controls Colombian pricing display and Wompi charges. Every change is audited.
            </p>
          </div>
        )}
      </Card>

      <Card
        title="Subscription Plan Limits"
        onEdit={() => setEditingPlanLimits(true)}
        editing={editingPlanLimits}
      >
        {editingPlanLimits ? (
          <PlanLimitsEditor
            planLimits={data.planLimits}
            onSave={handleSavePlanLimits}
            onCancel={() => setEditingPlanLimits(false)}
            saving={updateMutation.isPending}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 font-medium">Max Leads</th>
                  <th className="px-4 py-2 font-medium">Max Seats</th>
                      <th className="px-4 py-2 font-medium">Model Tier</th>
                  <th className="px-4 py-2 font-medium">Voice</th>
                  <th className="px-4 py-2 font-medium">WhatsApp</th>
                  <th className="px-4 py-2 font-medium">Custom Prompts</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlans.map(([plan, limits]) => (
                  <tr key={plan} className="border-b border-border">
                    <td className="px-4 py-2">
                      <Badge variant={planBadgeVariant(plan)}>{plan}</Badge>
                    </td>
                    <td className="px-4 py-2 text-text-secondary">
                      {formatNumber(limits.maxLeads)}
                    </td>
                    <td className="px-4 py-2 text-text-secondary">
                      {formatNumber(limits.maxSeats)}
                    </td>
                    <td className="px-4 py-2 text-text-secondary capitalize">{limits.modelTier}</td>
                    <td className="px-4 py-2">
                      <Badge variant={limits.voiceEnabled ? 'success' : 'default'}>
                        {limits.voiceEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={limits.whatsappEnabled ? 'success' : 'default'}>
                        {limits.whatsappEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={limits.customPrompts ? 'success' : 'default'}>
                        {limits.customPrompts ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ModelsCard models={data.supportedModels} />

      <Card
        title="Rate Limiting"
        onEdit={() => {
          setRateDraft({ ttl: data.rateLimits.ttl, limit: data.rateLimits.limit });
          setEditingRateLimits(true);
        }}
        editing={editingRateLimits}
      >
        {editingRateLimits && rateDraft ? (
          <div className="space-y-4">
            <div className="flex gap-6">
              <div className="space-y-1">
                <label className="text-xs text-text-muted block">TTL (seconds)</label>
                <input
                  type="number"
                  min={1}
                  value={rateDraft.ttl}
                  onChange={(e) =>
                    setRateDraft((prev) => prev && { ...prev, ttl: Number(e.target.value) })
                  }
                  className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text w-32"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-text-muted block">Max Requests</label>
                <input
                  type="number"
                  min={1}
                  value={rateDraft.limit}
                  onChange={(e) =>
                    setRateDraft((prev) => prev && { ...prev, limit: Number(e.target.value) })
                  }
                  className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text w-32"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setEditingRateLimits(false);
                  setRateDraft(null);
                }}
                disabled={updateMutation.isPending}
                className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
              >
                <X size={14} className="inline mr-1.5" />
                Cancel
              </button>
              <button
                onClick={handleSaveRateLimits}
                disabled={updateMutation.isPending}
                className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                <Check size={14} className="inline mr-1.5" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-8">
            <div>
              <span className="text-xs text-text-muted block">TTL (seconds)</span>
              <span className="text-sm text-text font-medium">{data.rateLimits.ttl}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted block">Max Requests</span>
              <span className="text-sm text-text font-medium">
                {formatNumber(data.rateLimits.limit)}
              </span>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="Platform Defaults"
        onEdit={() => {
          setDefaultsDraft({
            defaultMonthlyTokenLimit: data.defaultMonthlyTokenLimit,
            defaultModelTier: data.defaultModelTier,
          });
          setEditingDefaults(true);
        }}
        editing={editingDefaults}
      >
        {editingDefaults && defaultsDraft ? (
          <div className="space-y-4">
            <div className="flex gap-6">
              <div className="space-y-1">
                <label className="text-xs text-text-muted block">Monthly Token Limit</label>
                <input
                  type="number"
                  min={1}
                  value={defaultsDraft.defaultMonthlyTokenLimit}
                  onChange={(e) =>
                    setDefaultsDraft(
                      (prev) =>
                        prev && { ...prev, defaultMonthlyTokenLimit: Number(e.target.value) },
                    )
                  }
                  className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text w-40"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-text-muted block">Default Model Tier</label>
                <select
                  value={defaultsDraft.defaultModelTier}
                  onChange={(e) =>
                    setDefaultsDraft(
                      (prev) => prev && { ...prev, defaultModelTier: e.target.value },
                    )
                  }
                  className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
                >
                  {MODEL_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setEditingDefaults(false);
                  setDefaultsDraft(null);
                }}
                disabled={updateMutation.isPending}
                className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
              >
                <X size={14} className="inline mr-1.5" />
                Cancel
              </button>
              <button
                onClick={handleSaveDefaults}
                disabled={updateMutation.isPending}
                className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                <Check size={14} className="inline mr-1.5" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-8">
            <div>
              <span className="text-xs text-text-muted block">Monthly Token Limit</span>
              <span className="text-sm text-text font-medium">
                {formatNumber(data.defaultMonthlyTokenLimit)}
              </span>
            </div>
            <div>
              <span className="text-xs text-text-muted block">Default Model Tier</span>
              <span className="text-sm text-text font-medium capitalize">
                {data.defaultModelTier}
              </span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
